import axios from "axios";
import { app } from "../config/firebase";
import { UrlConstant } from "../commom/UrlConstant";
import Axios from "../config/axios";
import { LAST_TOKEN_KEY, releaseLocalPushToken, whenLocalPushReleased } from "./pushLocalCleanup";
import type {
  ForegroundPushMessage,
  RegisterDevicePayload,
  UnlinkDevicePayload,
} from "../types/push";

export const INSTALLATION_ID_KEY = "wolfpack_push_installation_id";
const SW_URL = "/firebase-messaging-sw.js";

export const isWebPushEnabled = (): boolean =>
  process.env.REACT_APP_WEB_PUSH_ENABLED === "true";

const warn = (msg: string, err?: unknown) => {
  // Never log tokens; only a message string from the error.
  const reason =
    err && typeof err === "object" && "message" in err
      ? String((err as any).message).slice(0, 200)
      : "";
  console.warn(`[push] ${msg}${reason ? `: ${reason}` : ""}`);
};

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStorage = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};

const getLoggedInUserToken = (): string | null => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(raw)?.userToken || null;
  } catch {
    return null;
  }
};

/** True if the browser has the pieces required for web push (sync part). */
export const hasBrowserPushApis = (): boolean =>
  typeof window !== "undefined" &&
  typeof Notification !== "undefined" &&
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator;

/** Full support check, including firebase/messaging's own isSupported(). */
export async function isPushSupported(): Promise<boolean> {
  if (!hasBrowserPushApis()) return false;
  try {
    const { isSupported } = await import("firebase/messaging");
    return await isSupported();
  } catch {
    return false;
  }
}

export function getInstallationId(): string {
  const existing = readStorage(INSTALLATION_ID_KEY);
  if (existing) return existing;
  let id: string;
  try {
    id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : fallbackUuid();
  } catch {
    id = fallbackUuid();
  }
  writeStorage(INSTALLATION_ID_KEY, id);
  return id;
}

function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const swRegistrationUrl = (): string => {
  const params = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  });
  return `${SW_URL}?${params.toString()}`;
};

let registering: Promise<boolean> | null = null;

/**
 * Registers this browser for web push. Runs only when: feature enabled, user
 * logged in, supported, and permission is ALREADY granted. Never prompts.
 * Returns true when the backend accepted the registration.
 */
export function registerDevice(): Promise<boolean> {
  if (registering) return registering;
  registering = doRegister().finally(() => {
    registering = null;
  });
  return registering;
}

async function doRegister(): Promise<boolean> {
  try {
    // A logout that is still deleting the old token must finish first, or it could delete the NEW token.
    await (unlinking || Promise.resolve());
    await whenLocalPushReleased();
    if (!isWebPushEnabled()) return false;
    if (!getLoggedInUserToken()) return false;
    if (!hasBrowserPushApis()) return false;
    if (Notification.permission !== "granted") return false;
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      warn("VAPID key missing; web push disabled");
      return false;
    }
    if (!(await isPushSupported())) return false;

    const { getMessaging, getToken } = await import("firebase/messaging");
    const swRegistration = await navigator.serviceWorker.register(
      swRegistrationUrl()
    );
    const messaging = getMessaging(app);
    const fcmToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });
    if (!fcmToken) return false;

    const installationId = getInstallationId();
    const lastToken = readStorage(LAST_TOKEN_KEY);
    // Re-register on every login/app start (server upserts); the stored token
    // is kept so unlink can reference it and token changes are detectable.
    const payload: RegisterDevicePayload = {
      installationId,
      fcmToken,
      platform: "web",
      appVersion: process.env.REACT_APP_VERSION || undefined,
    };
    await Axios.post(UrlConstant.Push.RegisterDevice, payload);
    if (lastToken !== fcmToken) writeStorage(LAST_TOKEN_KEY, fcmToken);
    return true;
  } catch (err) {
    warn("device registration failed", err);
    return false;
  }
}

/**
 * Prompts for notification permission. MUST only be called from an explicit
 * user gesture (e.g. a click handler). Returns the resulting permission.
 */
export async function requestPermissionFromUserAction(): Promise<
  NotificationPermission | "unsupported"
> {
  try {
    if (!isWebPushEnabled() || !hasBrowserPushApis()) return "unsupported";
    if (!(await isPushSupported())) return "unsupported";
    if (Notification.permission !== "default") return Notification.permission;
    return await Notification.requestPermission();
  } catch (err) {
    warn("permission request failed", err);
    return "unsupported";
  }
}

let unlinking: Promise<void> | null = null;

/**
 * Best-effort unlink of this web device. Safe to call right before the auth
 * token is cleared: the token is captured synchronously and passed explicitly,
 * uses plain axios (no 401 redirect side effects), never throws or blocks.
 * Afterwards the browser's FCM token is deleted too, even when the server call failed or no session is left.
 */
export function unlinkDevice(): Promise<void> {
  try {
    if (!isWebPushEnabled()) return Promise.resolve();
    const userToken = getLoggedInUserToken();
    const installationId = readStorage(INSTALLATION_ID_KEY) || undefined;
    const fcmToken = readStorage(LAST_TOKEN_KEY) || undefined;
    const serverCall: Promise<unknown> =
      userToken && (installationId || fcmToken)
        ? axios
            .post(UrlConstant.Push.UnlinkDevice, { installationId, fcmToken } as UnlinkDevicePayload, {
              headers: { Authorization: `Bearer ${userToken}` },
              timeout: 5000,
            })
            .catch((err) => warn("device unlink failed", err))
        : Promise.resolve();
    const chain: Promise<void> = serverCall
      .then(() => releaseLocalPushToken())
      .finally(() => {
        if (unlinking === chain) unlinking = null;
      });
    unlinking = chain;
    return chain;
  } catch (err) {
    warn("device unlink failed", err);
    return Promise.resolve();
  }
}

/** Subscribes to foreground messages. Returns an unsubscribe function. */
export async function listenForForegroundMessages(
  handler: (msg: ForegroundPushMessage) => void
): Promise<() => void> {
  try {
    if (!isWebPushEnabled() || !hasBrowserPushApis()) return () => { };
    if (!(await isPushSupported())) return () => { };
    const { getMessaging, onMessage } = await import("firebase/messaging");
    const unsubscribe = onMessage(getMessaging(app), (payload) => {
      try {
        const data = (payload?.data || {}) as Record<string, string>;
        handler({
          title: payload?.notification?.title || data.title || "Wolfpack",
          body: payload?.notification?.body || data.body || "",
          campaignId: data.campaignId,
          notificationId: data.notificationId,
          destination: data.destination ?? null,
        });
      } catch (err) {
        warn("foreground handler failed", err);
      }
    });
    return unsubscribe;
  } catch (err) {
    warn("foreground listener failed", err);
    return () => { };
  }
}
